import type { Client } from 'typesense';
import type { SearchResponseFacetCountSchema } from 'typesense/lib/Typesense/Documents';

import { buildTypesenseSearchParams, HITS_PER_PAGE } from './buildSearchParams';
import type { FacetItem, PetitionHitData, PetitionSearchParams, SearchResultsData } from './types';

const CACHE_SEARCH_RESULTS_FOR_SECONDS = 120;

function parseTopicFacets(
	facetCounts: Array<SearchResponseFacetCountSchema<PetitionHitData>> | undefined,
	selectedTopics: string[],
): FacetItem[] {
	if (!facetCounts) return [];
	const tagsFacet = facetCounts.find((f) => f.field_name === 'tags');
	if (!tagsFacet?.counts) return [];
	return tagsFacet.counts.map((c) => ({
		value: c.value,
		label: c.value,
		count: c.count,
		isRefined: selectedTopics.includes(c.value),
	}));
}

export type SearchClient = {
	searchPetitions: (params: PetitionSearchParams) => Promise<SearchResultsData>;
};

/**
 * Factory that abstracts the Typesense client behind domain functions. Pure and isomorphic
 * (no React/window deps) so it can be unit-tested with a mock client and reused in SSR prefetch.
 */
export function createSearchClient(client: Client, collectionName: string, preset: string): SearchClient {
	return {
		async searchPetitions(params: PetitionSearchParams): Promise<SearchResultsData> {
			const searchParams = buildTypesenseSearchParams(params, preset);

			const response = await client
				.collections<PetitionHitData>(collectionName)
				.documents()
				.search(searchParams, { cacheSearchResultsForSeconds: CACHE_SEARCH_RESULTS_FOR_SECONDS });

			return {
				hits: response.hits?.map((h) => h.document) ?? [],
				nbHits: response.found,
				nbPages: Math.ceil(response.found / HITS_PER_PAGE),
				query: response.request_params?.q || params.query,
				topicFacets: parseTopicFacets(response.facet_counts, params.topics),
			};
		},
	};
}
